import { Box, Collapse, Typography } from '@mui/material'
import React, { useRef } from 'react'
import { useIntl } from 'react-intl'
import type { Turn } from '../../shared/types'
import ChartBlock from './ChartBlock'
import MarkdownBlock from './MarkdownBlock'
import TableBlock from './TableBlock'
import ThinkingLog from './ThinkingLog'
import WorkingDots from './WorkingDots'

function shortThought(text: string): string {
  const t = text.replace(/\s+/g, ' ').trim()
  return t.length > 120 ? `${t.slice(0, 119)}…` : t
}

export default function AssistantTurn(
  { turn, pending }: { turn: Turn; pending?: { thought?: string } },
) {
  const intl = useIntl()
  const thoughts = turn.thoughts ?? []
  const hasAnswer =
    Boolean(turn.text) || (turn.tables?.length ?? 0) > 0 || (turn.charts?.length ?? 0) > 0
  const liveStatus = hasAnswer
    ? intl.formatMessage({ id: 'caChat.thread.working' })
    : pending?.thought
      ? shortThought(pending.thought)
      : intl.formatMessage({ id: 'caChat.thread.thinking' })
  const lastStatus = useRef(liveStatus)
  if (pending) {
    lastStatus.current = liveStatus
  }

  return (
    <Box sx={{ alignSelf: 'stretch', maxWidth: '100%', minWidth: 0 }}>
      {thoughts.length > 0 && <ThinkingLog thoughts={thoughts} live={!!pending} />}
      {turn.text && <MarkdownBlock text={turn.text} />}
      {turn.charts?.map((c, j) => <ChartBlock key={`c${j}`} chart={c} />)}
      {turn.tables?.map((tbl, j) => <TableBlock key={`t${j}`} data={tbl} />)}
      <Collapse in={!!pending} appear={false} unmountOnExit={true}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'text.secondary',
            mt: turn.text ? 1 : 0,
          }}
        >
          <WorkingDots />
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            {pending ? liveStatus : lastStatus.current}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  )
}
